import { NgModule } from '@angular/core';
import { mapToCanActivate, RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './components/home/home.component';
import { JoinGroupComponent } from './components/join-group/join-group.component';
import { LoginComponent } from './components/login/login.component';
import { RegisterComponent } from './components/register/register.component';
import { ProfileComponent } from './components/profile/profile.component';
import { AuthGuard } from './guards/auth.guard';
import { UnsavedChangesGuard } from './guards/unsaved-changes.guard';
import { GroupComponent } from './components/groups/group-header/group.component';
import { GroupOverviewComponent } from './components/groups/group-overview.component';
import { GroupPapersComponent } from './components/groups/group-papers.component';
import { GroupReadingListDetailComponent } from './components/groups/reading-list/group-reading-list-detail.component';
import { GroupReadingListsComponent } from './components/groups/reading-list/group-reading-lists.component';
import { GroupsPageComponent } from './components/groups/groups-overview/groups-page.component';
import { ProjectLayoutComponent } from './components/projects/project-layout.component';
import { ProjectsOverviewComponent } from './components/projects/projects-overview/projects-overview.component';
import { ProjectDetailComponent } from './components/projects/project-detail/project-detail.component';
import { ProjectFullTextRetrievalComponent } from './components/projects/project-full-text-retrieval/project-full-text-retrieval.component';
import { ProjectAnnotatorPageComponent } from './components/projects/project-annotator/project-annotator-page.component';
import { ProjectFirstStageScreeningComponent } from './components/projects/project-first-stage-screening/project-first-stage-screening.component';
import { ProjectCorpusComponent } from './components/projects/project-corpus/project-corpus.component';
import { AdminSetupComponent } from './components/admin/admin-setup/admin-setup.component';
import { AdminUsersComponent } from './components/admin/admin-users/admin-users.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ProjectConflictResolutionComponent } from './components/projects/project-conflict-resolution/project-conflict-resolution.component';
import { ProjectFullTextScreeningComponent } from './components/projects/project-full-text-screening/project-full-text-screening.component';
import { VerifyEmailComponent } from './components/verify-email/verify-email.component';
import { ProjectCompletedComponent } from './components/projects/project-completed/project-completed.component';
import { ProjectDataExtractionComponent } from './components/projects/project-data-extraction/project-data-extraction.component';
import { ProjectDataExtractionConflictsComponent } from './components/projects/project-data-extraction-conflicts/project-data-extraction-conflicts.component';
import { ProjectDataExtractionConflictDetailComponent } from './components/projects/project-data-extraction-conflict-detail/project-data-extraction-conflict-detail.component';
import { ProjectState } from './dtos/project-state';
import { GroupsOverviewComponent } from './components/groups/groups-overview/groups-overview.component';

const routes: Routes = [
  { path: '', component: HomeComponent },
  { path: 'join-group', component: JoinGroupComponent, canActivate: mapToCanActivate([AuthGuard]) },
  { path: 'login', component: LoginComponent },
  { path: 'register', component: RegisterComponent },
  { path: 'admin/setup', component: AdminSetupComponent },
  {
    path: 'admin/users',
    canActivate: mapToCanActivate([AuthGuard]),
    data: { roles: ['ADMIN', 'SUPER_ADMIN'] },
    component: AdminUsersComponent,
  },
  { path: 'profile', canActivate: mapToCanActivate([AuthGuard]), component: ProfileComponent },
  {
    path: 'groups',
    component: GroupsOverviewComponent,
    canActivate: mapToCanActivate([AuthGuard]),
  },
  {
    path: 'groups',
    canActivate: mapToCanActivate([AuthGuard]),
    children: [
      { path: '', component: GroupsPageComponent },
      {
        path: ':id',
        component: GroupComponent,
        children: [
          { path: '', component: GroupOverviewComponent },
          { path: 'papers', component: GroupPapersComponent },
          { path: 'reading-lists', component: GroupReadingListsComponent },
          { path: 'reading-lists/:readingListId', component: GroupReadingListDetailComponent },
        ],
      },
    ],
  },
  {
    path: 'projects',
    component: ProjectsOverviewComponent,
    canActivate: mapToCanActivate([AuthGuard]),
  },
  {
    path: 'projects/:projectId',
    canActivate: mapToCanActivate([AuthGuard]),
    component: ProjectLayoutComponent,
    children: [
      {
        path: 'setup',
        component: ProjectDetailComponent,
        data: { stage: ProjectState.SETUP },
      },
      {
        path: 'corpus',
        component: ProjectCorpusComponent,
        data: { stage: ProjectState.INGESTION },
      },
      {
        path: 'title-abstract-screening',
        component: ProjectFirstStageScreeningComponent,
        data: {
          stage: ProjectState.TITLE_ABSTRACT_SCREENING,
          pageTitle: 'Title & Abstract Screening',
        },
      },
      {
        path: 'full-text-retrieval',
        component: ProjectFullTextRetrievalComponent,
        data: { stage: ProjectState.FULL_TEXT_RETRIEVAL, pageTitle: 'Full-Text Retrieval' },
      },
      { path: 'full-text-screening', component: ProjectFullTextScreeningComponent },
      { path: 'conflict-resolution', component: ProjectConflictResolutionComponent },
      {
        path: 'annotator',
        component: ProjectAnnotatorPageComponent,
        data: { stage: ProjectState.FULL_TEXT_SCREENING, pageTitle: 'Full-Text Screening' },
      },
      { path: 'data-extraction', component: ProjectDataExtractionComponent },
      {
        path: 'data-extraction/conflicts',
        component: ProjectDataExtractionConflictsComponent,
      },
      {
        path: 'data-extraction/conflicts/:paperToProjectId',
        component: ProjectDataExtractionConflictDetailComponent,
        canDeactivate: [UnsavedChangesGuard],
      },
      { path: 'completed', component: ProjectCompletedComponent },
    ],
  },
  {
    path: 'verify-email',
    component: VerifyEmailComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  { path: '**', redirectTo: '' }, // maybe 404 later
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
